type DocumentLike = Record<string, any>;

const DOCUMENT_COMMENTS_STORAGE_KEY = "verdeo.document-comments";

const getDocumentId = (document: DocumentLike | null | undefined): string | null => {
  const id = Number(document?.id ?? document?.media?.id ?? 0);

  return Number.isFinite(id) && id > 0 ? String(id) : null;
};

const readStoredComments = (): Record<string, string> => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(DOCUMENT_COMMENTS_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : {};

    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeStoredComments = (comments: Record<string, string>) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(DOCUMENT_COMMENTS_STORAGE_KEY, JSON.stringify(comments));
  } catch {
    // Le commentaire reste visible dans le cache courant meme si le stockage local est indisponible.
  }
};

export const rememberDocumentComments = (documents: DocumentLike[], comment?: string | null) => {
  const trimmedComment = comment?.trim();
  if (!trimmedComment) {
    return;
  }

  const storedComments = readStoredComments();
  let hasChanged = false;

  documents.forEach((document) => {
    const id = getDocumentId(document);
    if (!id) {
      return;
    }

    storedComments[id] = trimmedComment;
    hasChanged = true;
  });

  if (hasChanged) {
    writeStoredComments(storedComments);
  }
};

export const getRememberedDocumentComment = (document: DocumentLike | null | undefined) => {
  const id = getDocumentId(document);
  if (!id) {
    return null;
  }

  return readStoredComments()[id] ?? null;
};

export const withDocumentComment = (document: DocumentLike, comment?: string | null) => {
  const trimmedComment = comment?.trim();
  if (!trimmedComment) {
    return document;
  }

  return {
    ...document,
    description: document?.description || trimmedComment,
    comment: document?.comment || trimmedComment,
  };
};

export const applyRememberedDocumentComments = (payload: any): any => {
  const storedComments = readStoredComments();

  const applyComment = (value: any): any => {
    if (Array.isArray(value)) {
      return value.map(applyComment);
    }

    if (!value || typeof value !== "object") {
      return value;
    }

    const id = getDocumentId(value);
    const rememberedComment = id ? storedComments[id] : null;
    const nextValue = Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, applyComment(nestedValue)]),
    );

    return rememberedComment ? withDocumentComment(nextValue, rememberedComment) : nextValue;
  };

  return applyComment(payload);
};
